import { useTranslation } from '@jq4race/i18n';
import type { InvitationListItem } from '@jq4race/shared';
import {
  Badge,
  Button,
  Input,
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from '@jq4race/ui';
import { RotateCw, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { formatDateTime } from '../../../../shared/format/format-date';
import { Pagination } from './Pagination';
import { Select } from './Select';

type StatusFilter = 'all' | InvitationListItem['status'];
type SortKey = 'email' | 'role' | 'status' | 'expiresAt';
type SortDir = 'asc' | 'desc';

const PAGE_SIZE = 10;

const STATUS_LABEL_KEYS: Record<InvitationListItem['status'], string> = {
  pending: 'invitationsTable.status.pending',
  expired: 'invitationsTable.status.expired',
  used: 'invitationsTable.status.used',
};

const STATUS_ORDER: Record<InvitationListItem['status'], number> = {
  pending: 0,
  expired: 1,
  used: 2,
};

const ROLE_LABEL_KEYS: Record<InvitationListItem['role'], string> = {
  super_admin: 'roles.superAdmin',
  org_admin: 'roles.orgAdmin',
  supervisor: 'roles.supervisor',
  operator: 'roles.operator',
};

interface InvitationsTableProps {
  invitations: InvitationListItem[];
  loading?: boolean;
  /** When provided, an Actions column renders a Resend button for pending/expired rows. */
  onResend?: (invitationId: string) => Promise<void>;
}

export function InvitationsTable({
  invitations,
  loading = false,
  onResend,
}: InvitationsTableProps) {
  const { t, i18n } = useTranslation('users');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sortKey, setSortKey] = useState<SortKey>('expiresAt');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [page, setPage] = useState(1);
  const [resendingId, setResendingId] = useState<string | null>(null);

  const colCount = onResend ? 5 : 4;

  async function handleResend(id: string) {
    if (!onResend || resendingId) return;
    setResendingId(id);
    try {
      await onResend(id);
    } finally {
      setResendingId(null);
    }
  }

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return invitations.filter((inv) => {
      if (term && !inv.email.toLowerCase().includes(term)) return false;
      if (statusFilter !== 'all' && inv.status !== statusFilter) return false;
      return true;
    });
  }, [invitations, search, statusFilter]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const dir = sortDir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      switch (sortKey) {
        case 'email':
          return a.email.localeCompare(b.email) * dir;
        case 'role':
          return a.role.localeCompare(b.role) * dir;
        case 'status':
          return (STATUS_ORDER[a.status] - STATUS_ORDER[b.status]) * dir;
        case 'expiresAt':
          return (new Date(a.expiresAt).getTime() - new Date(b.expiresAt).getTime()) * dir;
        default:
          return 0;
      }
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = sorted.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  const sortFor = (key: SortKey): SortDir | null => (sortKey === key ? sortDir : null);

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative max-w-sm flex-1">
          <Search
            aria-hidden
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-jq-stone"
            strokeWidth={1.5}
          />
          <Input
            type="search"
            placeholder={t('invitationsTable.searchPlaceholder')}
            className="h-9 pl-9 text-[13px]"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>

        <Select
          selectSize="sm"
          aria-label={t('invitationsTable.filterByStatus')}
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value as StatusFilter);
            setPage(1);
          }}
          className="min-w-[140px] sm:w-auto"
        >
          <option value="all">{t('invitationsTable.allStatuses')}</option>
          <option value="pending">{t('invitationsTable.status.pending')}</option>
          <option value="expired">{t('invitationsTable.status.expired')}</option>
          <option value="used">{t('invitationsTable.status.used')}</option>
        </Select>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead
              sortable
              sortDirection={sortFor('email')}
              onClick={() => toggleSort('email')}
            >
              {t('invitationsTable.columns.email')}
            </TableHead>
            <TableHead sortable sortDirection={sortFor('role')} onClick={() => toggleSort('role')}>
              {t('invitationsTable.columns.role')}
            </TableHead>
            <TableHead
              sortable
              sortDirection={sortFor('status')}
              onClick={() => toggleSort('status')}
            >
              {t('invitationsTable.columns.status')}
            </TableHead>
            <TableHead
              sortable
              sortDirection={sortFor('expiresAt')}
              onClick={() => toggleSort('expiresAt')}
            >
              {t('invitationsTable.columns.expires')}
            </TableHead>
            {onResend ? (
              <TableHead className="w-[100px] text-right">
                {t('invitationsTable.columns.actions')}
              </TableHead>
            ) : null}
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading && invitations.length === 0 ? (
            <TableSkeleton
              rows={5}
              columns={colCount}
              cellWidths={['w-[200px]', 'w-[120px]', 'w-[72px] rounded-full', 'w-[140px]']}
            />
          ) : paged.length === 0 ? (
            <TableEmpty
              colSpan={colCount}
              message={
                invitations.length === 0
                  ? t('invitationsTable.empty')
                  : t('invitationsTable.emptyFiltered')
              }
            />
          ) : (
            paged.map((inv) => (
              <TableRow key={inv.id}>
                <TableCell>
                  <span className="font-medium text-jq-charcoal">{inv.email}</span>
                </TableCell>
                <TableCell>{t(ROLE_LABEL_KEYS[inv.role])}</TableCell>
                <TableCell>
                  <Badge variant={statusVariant(inv.status)}>
                    {t(STATUS_LABEL_KEYS[inv.status])}
                  </Badge>
                </TableCell>
                <TableCell muted>{formatDateTime(inv.expiresAt, i18n.language)}</TableCell>
                {onResend ? (
                  <TableCell className="text-right">
                    {inv.status !== 'used' ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={resendingId !== null}
                        onClick={() => void handleResend(inv.id)}
                      >
                        <RotateCw
                          className={`h-3.5 w-3.5 ${resendingId === inv.id ? 'animate-spin' : ''}`}
                          strokeWidth={1.75}
                        />
                        {t('invitationsTable.resend')}
                      </Button>
                    ) : null}
                  </TableCell>
                ) : null}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <Pagination
        total={sorted.length}
        page={safePage}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
      />
    </div>
  );
}

function statusVariant(status: InvitationListItem['status']): 'success' | 'warning' | 'danger' {
  if (status === 'used') return 'success';
  if (status === 'expired') return 'danger';
  return 'warning';
}
